import React from 'react';
import { Badge, DataTable } from '../Ui';
import { STAFF_ROLES } from '../../config/plans';

function fmtDate(iso) {
  if (!iso) return '—';
  return iso.slice(0, 16).replace('T', ' ');
}

function fmtInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

export function CountGrid({ counts }) {
  if (!counts) return null;
  const items = [
    ['Doctors', counts.doctors],
    ['Reception', counts.reception],
    ['Pharmacy staff', counts.pharmacyStaff],
    ['Lab staff', counts.labStaff],
    ['Billing', counts.billingStaff],
    ['Nurses', counts.nurses],
    ['Managers', counts.managers],
    ['Patients', counts.patients],
    ['OP queue', counts.queueActive],
    ['Receipts', counts.receiptsAll],
    ['IP active', counts.admissionsActive],
    ['Beds', counts.bedsTotal],
    ['Pharmacy orders', counts.pharmacyOrders],
    ['Lab orders', counts.labOrders],
    ['EMR records', counts.emrRecords],
    ['Audit events', counts.auditEvents],
  ];
  return (
    <div className="monitor-count-grid">
      {items.map(([label, val]) => (
        <div key={label} className="monitor-count-cell">
          <span className="monitor-count-val">{val ?? 0}</span>
          <span className="monitor-count-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

const staffCols = [
  { key: 'name', label: 'Name', render: (r) => (
    <div><strong>{r.name}</strong>{r.specialty && <div className="hint">{r.specialty}</div>}</div>
  ) },
  { key: 'email', label: 'Login email' },
  { key: 'role', label: 'Role' },
  { key: 'phone', label: 'Phone', render: (r) => r.phone || '—' },
  { key: 'active', label: 'Status', render: (r) => (
    <Badge tone={r.active !== false ? 'success' : 'warning'}>{r.active !== false ? 'Active' : 'Off'}</Badge>
  ) },
  { key: 'createdAt', label: 'Created', render: (r) => fmtDate(r.createdAt) },
];

export function StaffRoleSection({ title, rows }) {
  return (
    <div className="card mb">
      <h3>{title} ({rows.length})</h3>
      <DataTable columns={staffCols} rows={rows} keyField="staffId" emptyMessage="None created yet." />
    </div>
  );
}

export function StaffAllRoles({ staffByRole }) {
  return (
    <div>
      {STAFF_ROLES.map((r) => (
        <StaffRoleSection key={r.id} title={r.label} rows={staffByRole?.[r.id] || []} />
      ))}
    </div>
  );
}

export const patientCols = [
  { key: 'patientId', label: 'UHID' },
  { key: 'name', label: 'Name' },
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Sex' },
  { key: 'phone', label: 'Phone' },
  { key: 'createdAt', label: 'Registered', render: (r) => fmtDate(r.createdAt) },
];

export const queueCols = [
  { key: 'patientName', label: 'Patient' },
  { key: 'doctor', label: 'Doctor' },
  { key: 'status', label: 'Status' },
  { key: 'fee', label: 'Fee', render: (r) => fmtInr(r.fee) },
  { key: 'time', label: 'Time' },
  { key: 'createdAt', label: 'Added', render: (r) => fmtDate(r.createdAt) },
];

export const receiptCols = [
  { key: 'createdAt', label: 'Date', render: (r) => fmtDate(r.createdAt) },
  { key: 'patientName', label: 'Patient' },
  { key: 'type', label: 'Type' },
  { key: 'amount', label: 'Amount', render: (r) => fmtInr(r.amount) },
  { key: 'receiptId', label: 'Receipt ID' },
];

export const admissionCols = [
  { key: 'patientName', label: 'Patient' },
  { key: 'bed', label: 'Bed' },
  { key: 'ward', label: 'Ward' },
  { key: 'status', label: 'Status' },
  { key: 'pendingBill', label: 'Outstanding', render: (r) => fmtInr(r.pendingBill) },
  { key: 'admittedAt', label: 'Admitted', render: (r) => fmtDate(r.admittedAt || r.createdAt) },
];

export const bedCols = [
  { key: 'bedId', label: 'Bed ID' },
  { key: 'ward', label: 'Ward' },
  { key: 'room', label: 'Room' },
  { key: 'bed', label: 'Bed' },
  { key: 'ratePerDay', label: 'Rate/day', render: (r) => fmtInr(r.ratePerDay) },
  { key: 'status', label: 'Status', render: (r) => (
    <Badge tone={r.status === 'vacant' ? 'success' : 'warning'}>{r.status}</Badge>
  ) },
];

export const pharmacyCols = [
  { key: 'orderId', label: 'Order' },
  { key: 'patientName', label: 'Patient' },
  { key: 'items', label: 'Items' },
  { key: 'amount', label: 'Amount', render: (r) => fmtInr(r.amount) },
  { key: 'status', label: 'Status' },
  { key: 'createdAt', label: 'Created', render: (r) => fmtDate(r.createdAt) },
];

export const labCols = [
  { key: 'orderId', label: 'Order' },
  { key: 'patientName', label: 'Patient' },
  { key: 'test', label: 'Test' },
  { key: 'amount', label: 'Amount', render: (r) => fmtInr(r.amount) },
  { key: 'status', label: 'Status' },
  { key: 'createdAt', label: 'Created', render: (r) => fmtDate(r.createdAt) },
];

export const emrCols = [
  { key: 'recordId', label: 'Record' },
  { key: 'patientName', label: 'Patient' },
  { key: 'doctor', label: 'Doctor' },
  { key: 'diagnosis', label: 'Diagnosis', render: (r) => (r.diagnosis || r.chiefComplaint || '—').slice(0, 60) },
  { key: 'createdAt', label: 'Visit', render: (r) => fmtDate(r.createdAt) },
];

export const auditCols = [
  { key: 'createdAt', label: 'When', render: (r) => fmtDate(r.createdAt) },
  { key: 'action', label: 'Action' },
  { key: 'email', label: 'User' },
  { key: 'role', label: 'Role' },
  { key: 'by', label: 'By', render: (r) => r.by || '—' },
];
